import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Move, GripVertical } from 'lucide-react';

// Visual mm → px scale on the canvas (must be multiple of GRID for snapping)
const PX_PER_MM = 4;
const GRID = 5; // mm

const fieldDisplayLabel = (f) => {
  if (f.field_type === 'qr') return 'QR';
  if (f.field_type === 'barcode') return '|||';
  if (f.field_type === 'text') return f.static_value || f.field_name;
  if (f.field_type === 'linked') return `{${f.field_name}}`;
  return f.field_name;
};

/**
 * LabelCanvas — visually positions and resizes label fields on a 2D canvas
 * sized to the template's width_mm × height_mm. Persists to x_position,
 * y_position, width, height (mm) and font_size (pt) on each field.
 */
const LabelCanvas = ({ template, onChange }) => {
  const canvasRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null); // { fieldId, startX, startY, mode: 'move'|'resize' }

  const widthMm = parseInt(template.width_mm) || 100;
  const heightMm = parseInt(template.height_mm) || 50;
  const canvasW = widthMm * PX_PER_MM;
  const canvasH = heightMm * PX_PER_MM;

  const updateField = (fieldId, patch) => {
    const next = (template.fields || []).map((f) => (f.id === fieldId ? { ...f, ...patch } : f));
    onChange(next);
  };

  const snap = (mm) => Math.round(mm / GRID) * GRID;

  const handleMouseDown = (e, field, mode) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(field.id);
    const rect = canvasRef.current.getBoundingClientRect();
    setDrag({
      fieldId: field.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: field.x_position || 0,
      startY: field.y_position || 0,
      startW: field.width || 30,
      startH: field.height || 10,
      canvasRect: rect,
      mode,
    });
  };

  // Use real onMouseMove with the actual stored start values
  const onMove = (e) => {
    if (!drag) return;
    const dxPx = e.clientX - drag.startMouseX;
    const dyPx = e.clientY - drag.startMouseY;
    const dxMm = dxPx / PX_PER_MM;
    const dyMm = dyPx / PX_PER_MM;
    if (drag.mode === 'move') {
      const newX = Math.max(0, Math.min(widthMm - 5, snap(drag.startX + dxMm)));
      const newY = Math.max(0, Math.min(heightMm - 5, snap(drag.startY + dyMm)));
      updateField(drag.fieldId, { x_position: newX, y_position: newY });
    } else {
      const newW = Math.max(5, Math.min(widthMm - drag.startX, snap(drag.startW + dxMm)));
      const newH = Math.max(4, Math.min(heightMm - drag.startY, snap(drag.startH + dyMm)));
      updateField(drag.fieldId, { width: newW, height: newH });
    }
  };

  const onUp = () => setDrag(null);

  const selected = (template.fields || []).find((f) => f.id === selectedId);

  return (
    <div className="space-y-3" data-testid="label-canvas-wrap">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span><Move className="inline w-3 h-3 mr-1" /> Drag fields to position. Drag the bottom-right handle to resize. Snaps to {GRID}mm grid.</span>
        <span className="mono">{widthMm} × {heightMm} mm</span>
      </div>
      <div
        ref={canvasRef}
        className="relative bg-white border border-slate-300 mx-auto select-none"
        style={{
          width: `${canvasW}px`,
          height: `${canvasH}px`,
          backgroundImage: `linear-gradient(to right, #f1f5f9 1px, transparent 1px), linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)`,
          backgroundSize: `${GRID * PX_PER_MM}px ${GRID * PX_PER_MM}px`,
        }}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onClick={() => setSelectedId(null)}
        data-testid="label-canvas"
      >
        {(template.fields || []).map((f, idx) => {
          const x = (f.x_position || 0) * PX_PER_MM;
          const y = (f.y_position || 0) * PX_PER_MM;
          const w = Math.max(20, (f.width || 30) * PX_PER_MM);
          const h = Math.max(16, (f.height || 10) * PX_PER_MM);
          const isSelected = selectedId === f.id;
          return (
            <div
              key={f.id || `${f.field_name || 'fld'}-${idx}`}
              className={`absolute rounded-sm flex items-center justify-center text-xs cursor-move overflow-hidden ${
                isSelected ? 'bg-orange-100 border-2 border-orange-500 ring-2 ring-orange-200' : 'bg-slate-100 border border-slate-300 hover:border-orange-400'
              }`}
              style={{ left: x, top: y, width: w, height: h, fontSize: Math.max(8, (f.font_size || 12) * 0.7) }}
              onMouseDown={(e) => handleMouseDown(e, f, 'move')}
              onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}
              data-testid={`canvas-field-${f.field_name}`}
            >
              {f.field_type === 'qr' ? (
                <div className="flex items-center justify-center w-full h-full p-0.5">
                  <QRCodeSVG value="preview" size={Math.min(w, h) - 6} level="M" includeMargin={false} />
                </div>
              ) : (
                <span className="px-1 truncate text-slate-700">{fieldDisplayLabel(f)}</span>
              )}
              {/* resize handle */}
              <div
                className="absolute right-0 bottom-0 w-3 h-3 bg-orange-500 cursor-nwse-resize rounded-tl-sm"
                onMouseDown={(e) => handleMouseDown(e, f, 'resize')}
                data-testid={`canvas-resize-${f.field_name}`}
              >
                <GripVertical className="w-2 h-2 text-white" />
              </div>
            </div>
          );
        })}
        {(template.fields || []).length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
            Add fields above — they'll appear here for placement.
          </div>
        )}
      </div>

      {selected && (
        <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="canvas-selected-controls">
          <div>
            <div className="text-slate-500">Selected</div>
            <div className="font-medium text-slate-800 truncate">{selected.field_name}</div>
          </div>
          <div>
            <label className="text-slate-500 block">X (mm)</label>
            <input
              type="number"
              min={0}
              max={widthMm}
              step={GRID}
              value={selected.x_position || 0}
              onChange={(e) => updateField(selected.id, { x_position: snap(parseFloat(e.target.value) || 0) })}
              className="w-full rounded-sm border border-slate-300 px-2 py-1 mono"
            />
          </div>
          <div>
            <label className="text-slate-500 block">Y (mm)</label>
            <input
              type="number"
              min={0}
              max={heightMm}
              step={GRID}
              value={selected.y_position || 0}
              onChange={(e) => updateField(selected.id, { y_position: snap(parseFloat(e.target.value) || 0) })}
              className="w-full rounded-sm border border-slate-300 px-2 py-1 mono"
            />
          </div>
          <div>
            <label className="text-slate-500 block">Font (pt)</label>
            <input
              type="number"
              min={6}
              max={48}
              value={selected.font_size || 12}
              onChange={(e) => updateField(selected.id, { font_size: parseInt(e.target.value) || 12 })}
              className="w-full rounded-sm border border-slate-300 px-2 py-1 mono"
            />
          </div>
          <div>
            <label className="text-slate-500 block">W (mm)</label>
            <input
              type="number"
              min={5}
              max={widthMm}
              step={GRID}
              value={selected.width || 30}
              onChange={(e) => updateField(selected.id, { width: snap(parseFloat(e.target.value) || 5) })}
              className="w-full rounded-sm border border-slate-300 px-2 py-1 mono"
            />
          </div>
          <div>
            <label className="text-slate-500 block">H (mm)</label>
            <input
              type="number"
              min={4}
              max={heightMm}
              step={GRID}
              value={selected.height || 10}
              onChange={(e) => updateField(selected.id, { height: snap(parseFloat(e.target.value) || 4) })}
              className="w-full rounded-sm border border-slate-300 px-2 py-1 mono"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelCanvas;
